// LeavePanel — review staff leave applications, approve or reject.
import { useEffect, useState } from 'react';
import { getLeaveRequests, decideLeaveRequest } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';

const STATUS_TONE = { pending: 'caution', approved: 'success', rejected: 'critical' };

export default function LeavePanel() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [msg, setMsg] = useState('');

  useEffect(() => { load(); }, [filter]);

  async function load() {
    try { setRequests(await getLeaveRequests(filter === 'all' ? undefined : filter)); }
    catch { setMsg('Failed to load leave requests'); }
  }

  async function decide(id, status) {
    try {
      await decideLeaveRequest(id, status);
      setMsg(`Request ${status} ✅`);
      load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  function workingDays(start, end) {
    const s = new Date(start), e = new Date(end);
    let count = 0;
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      if (day !== 0) count++; // exclude Sunday only (Saturday is a school day per day_of_week 1-6)
    }
    return count;
  }

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Leave Requests</h2>
      {msg && (
        <p className={`text-sm font-bold px-3 py-2 rounded-s mb-4 inline-block ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
          {msg}
        </p>
      )}

      <div className="flex gap-2 mb-4">
        {['pending', 'approved', 'rejected', 'all'].map(f => (
          <Button key={f} variant={filter === f ? 'primary' : 'secondary'} onClick={() => setFilter(f)}>
            {f[0].toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader title={`Leave Requests (${requests.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                {['Applicant', 'Type', 'From', 'To', 'Days', 'Reason', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-2.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3">{r.applicant_name} <span className="text-ink-soft text-xs">({r.applicant_role})</span></td>
                  <td className="px-4 py-3">{r.leave_type}</td>
                  <td className="px-4 py-3">{r.start_date}</td>
                  <td className="px-4 py-3">{r.end_date}</td>
                  <td className="px-4 py-3 tabular-nums">{workingDays(r.start_date, r.end_date)}</td>
                  <td className="px-4 py-3 max-w-[280px]">{r.reason}</td>
                  <td className="px-4 py-3"><Badge tone={STATUS_TONE[r.status] || 'neutral'}>{r.status}</Badge></td>
                  <td className="px-4 py-3">
                    {r.status === 'pending' && (
                      <div className="flex gap-1.5">
                        <Button variant="primary" className="!px-2.5 !py-1 !text-xs" onClick={() => decide(r.id, 'approved')}>Approve</Button>
                        <Button variant="danger" className="!px-2.5 !py-1 !text-xs" onClick={() => decide(r.id, 'rejected')}>Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!requests.length && (
                <tr><td className="px-4 py-8 text-center text-ink-soft" colSpan={8}>No leave requests.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
